import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { TextSelection } from '@tiptap/pm/state';
import { useState, useEffect, useRef } from 'react';

/**
 * TipTap Editor Configuration
 * Provides a configured editor instance with common extensions
 */
export const useTipTapEditor = (options = {}) => {
  const {
    content = null,
    placeholder = 'Start writing your note...',
    editable = true,
    onUpdate = null,
    onCreate = null,
    onFocus = null,
    onBlur = null
  } = options;

  return useEditor({
    extensions: [
      StarterKit.configure({
        // Configure heading levels
        heading: {
          levels: [1, 2, 3]
        },
        // Lists are enabled by default in StarterKit
        bulletList: true,
        orderedList: true,
        listItem: true,
        // Disable code block if needed (can be enabled later)
        // codeBlock: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline hover:text-primary-dark',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder,
      }),
    ],
    content: content,
    editable: editable,
    onUpdate: ({ editor }) => {
      if (onUpdate) {
        onUpdate(editor.getJSON());
      }
    },
    onCreate: ({ editor }) => {
      if (onCreate) {
        onCreate(editor.getJSON());
      }
    },
    onFocus: () => {
      if (onFocus) {
        onFocus();
      }
    },
    onBlur: () => {
      if (onBlur) {
        onBlur();
      }
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor-content',
        style: 'font-size: 12px; min-height: 200px; padding: 16px; outline: none;',
      },
      handleKeyDown: (view, event) => {
        // Handle Ctrl+A / Cmd+A for select all
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a' && !event.shiftKey) {
          // Use ProseMirror's TextSelection to select all
          const { state, dispatch } = view;
          const { tr } = state;
          const selection = TextSelection.create(tr.doc, 0, tr.doc.content.size);
          tr.setSelection(selection);
          dispatch(tr);
          return true; // Handled
        }
        return false; // Let TipTap handle other keys
      },
    },
  });
};

/**
 * TipTap Toolbar Component
 * Simple toolbar with common formatting options
 * On mobile, combines H1/H2/H3 and lists into dropdowns
 */
export const TipTapToolbar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [headingDropdownOpen, setHeadingDropdownOpen] = useState(false);
  const [listDropdownOpen, setListDropdownOpen] = useState(false);
  const headingDropdownRef = useRef(null);
  const listDropdownRef = useRef(null);

  const isBold = editor.isActive('bold');
  const isItalic = editor.isActive('italic');
  const isH1 = editor.isActive('heading', { level: 1 });
  const isH2 = editor.isActive('heading', { level: 2 });
  const isH3 = editor.isActive('heading', { level: 3 });
  const isBulletList = editor.isActive('bulletList');
  const isOrderedList = editor.isActive('orderedList');
  const isBlockquote = editor.isActive('blockquote');

  // Get current heading level for display
  const currentHeading = isH1 ? 'H1' : isH2 ? 'H2' : isH3 ? 'H3' : 'H';
  const currentList = isBulletList ? '•' : isOrderedList ? '1.' : 'List';

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headingDropdownRef.current && !headingDropdownRef.current.contains(event.target)) {
        setHeadingDropdownOpen(false);
      }
      if (listDropdownRef.current && !listDropdownRef.current.contains(event.target)) {
        setListDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const buttonStyle = {
    flex: 1,
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '13px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    position: 'relative',
  };

  const getButtonStyle = (isActive) => ({
    ...buttonStyle,
    backgroundColor: isActive ? '#2CA6A4' : '#ffffff',
    color: isActive ? '#ffffff' : '#374151',
  });

  const handleHeadingClick = (level) => {
    editor.chain().focus().toggleHeading({ level }).run();
    setHeadingDropdownOpen(false);
  };

  const handleListClick = (type) => {
    if (type === 'bullet') {
      editor.chain().focus().toggleBulletList().run();
    } else {
      editor.chain().focus().toggleOrderedList().run();
    }
    setListDropdownOpen(false);
  };

  return (
    <div className="border-b border-gray-200 p-1 flex gap-0.5 bg-gray-50" style={{ flexWrap: 'nowrap', position: 'relative' }}>
      {/* Bold Button */}
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        style={{
          ...getButtonStyle(isBold),
          fontWeight: 'bold',
        }}
        onMouseEnter={(e) => {
          if (!isBold) {
            e.target.style.backgroundColor = '#f3f4f6';
          }
        }}
        onMouseLeave={(e) => {
          if (!isBold) {
            e.target.style.backgroundColor = '#ffffff';
          }
        }}
        title="Bold"
      >
        B
      </button>

      {/* Italic Button */}
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        style={{
          ...getButtonStyle(isItalic),
          fontStyle: 'italic',
        }}
        onMouseEnter={(e) => {
          if (!isItalic) {
            e.target.style.backgroundColor = '#f3f4f6';
          }
        }}
        onMouseLeave={(e) => {
          if (!isItalic) {
            e.target.style.backgroundColor = '#ffffff';
          }
        }}
        title="Italic"
      >
        I
      </button>

      {/* Heading Dropdown (Mobile) or Individual Buttons (Desktop) */}
      {isMobile ? (
        <div ref={headingDropdownRef} style={{ flex: 1, position: 'relative' }}>
          <button
            type="button"
            onClick={() => setHeadingDropdownOpen(!headingDropdownOpen)}
            style={{
              ...getButtonStyle(isH1 || isH2 || isH3),
              fontSize: isH1 ? '18px' : isH2 ? '15px' : isH3 ? '13px' : '13px',
              fontWeight: isH1 ? 'bold' : isH2 ? '600' : isH3 ? '500' : 'normal',
              lineHeight: '1.2',
              width: '100%',
            }}
            onMouseEnter={(e) => {
              if (!isH1 && !isH2 && !isH3) {
                e.target.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (!isH1 && !isH2 && !isH3) {
                e.target.style.backgroundColor = '#ffffff';
              }
            }}
            title="Headings"
          >
            {currentHeading}
          </button>
          {headingDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                marginTop: '2px',
              }}
            >
              <button
                type="button"
                onClick={() => handleHeadingClick(1)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  textAlign: 'left',
                  backgroundColor: isH1 ? '#f3f4f6' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isH1 ? '#f3f4f6' : 'transparent';
                }}
              >
                H1
              </button>
              <button
                type="button"
                onClick={() => handleHeadingClick(2)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  textAlign: 'left',
                  backgroundColor: isH2 ? '#f3f4f6' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isH2 ? '#f3f4f6' : 'transparent';
                }}
              >
                H2
              </button>
              <button
                type="button"
                onClick={() => handleHeadingClick(3)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  textAlign: 'left',
                  backgroundColor: isH3 ? '#f3f4f6' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isH3 ? '#f3f4f6' : 'transparent';
                }}
              >
                H3
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* H1 Button (Desktop) */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            style={{
              ...getButtonStyle(isH1),
              fontSize: isH1 ? '13px' : '18px',
              fontWeight: 'bold',
              lineHeight: '1.2',
            }}
            onMouseEnter={(e) => {
              if (!isH1) {
                e.target.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (!isH1) {
                e.target.style.backgroundColor = '#ffffff';
              }
            }}
            title="Heading 1"
          >
            H1
          </button>

          {/* H2 Button (Desktop) */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            style={{
              ...getButtonStyle(isH2),
              fontSize: isH2 ? '13px' : '15px',
              fontWeight: '600',
              lineHeight: '1.2',
            }}
            onMouseEnter={(e) => {
              if (!isH2) {
                e.target.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (!isH2) {
                e.target.style.backgroundColor = '#ffffff';
              }
            }}
            title="Heading 2"
          >
            H2
          </button>

          {/* H3 Button (Desktop) */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            style={{
              ...getButtonStyle(isH3),
              fontSize: isH3 ? '13px' : '13px',
              fontWeight: '500',
              lineHeight: '1.2',
            }}
            onMouseEnter={(e) => {
              if (!isH3) {
                e.target.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (!isH3) {
                e.target.style.backgroundColor = '#ffffff';
              }
            }}
            title="Heading 3"
          >
            H3
          </button>
        </>
      )}

      {/* List Dropdown (Mobile) or Individual Buttons (Desktop) */}
      {isMobile ? (
        <div ref={listDropdownRef} style={{ flex: 1, position: 'relative' }}>
          <button
            type="button"
            onClick={() => setListDropdownOpen(!listDropdownOpen)}
            style={{
              ...getButtonStyle(isBulletList || isOrderedList),
              fontSize: isBulletList ? '16px' : isOrderedList ? '13px' : '13px',
              width: '100%',
            }}
            onMouseEnter={(e) => {
              if (!isBulletList && !isOrderedList) {
                e.target.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (!isBulletList && !isOrderedList) {
                e.target.style.backgroundColor = '#ffffff';
              }
            }}
            title="Lists"
          >
            {currentList}
          </button>
          {listDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#ffffff',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                marginTop: '2px',
              }}
            >
              <button
                type="button"
                onClick={() => handleListClick('bullet')}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  textAlign: 'center',
                  backgroundColor: isBulletList ? '#f3f4f6' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isBulletList ? '#f3f4f6' : 'transparent';
                }}
              >
                •
              </button>
              <button
                type="button"
                onClick={() => handleListClick('ordered')}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  textAlign: 'center',
                  backgroundColor: isOrderedList ? '#f3f4f6' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isOrderedList ? '#f3f4f6' : 'transparent';
                }}
              >
                1.
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Bullet List Button (Desktop) */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            style={{
              ...getButtonStyle(isBulletList),
              fontSize: '16px',
            }}
            onMouseEnter={(e) => {
              if (!isBulletList) {
                e.target.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (!isBulletList) {
                e.target.style.backgroundColor = '#ffffff';
              }
            }}
            title="Bullet List"
          >
            •
          </button>

          {/* Ordered List Button (Desktop) */}
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            style={{
              ...getButtonStyle(isOrderedList),
              fontSize: '13px',
            }}
            onMouseEnter={(e) => {
              if (!isOrderedList) {
                e.target.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (!isOrderedList) {
                e.target.style.backgroundColor = '#ffffff';
              }
            }}
            title="Numbered List"
          >
            1.
          </button>
        </>
      )}

      {/* Blockquote Button */}
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        style={{
          ...getButtonStyle(isBlockquote),
          fontSize: '14px',
        }}
        onMouseEnter={(e) => {
          if (!isBlockquote) {
            e.target.style.backgroundColor = '#f3f4f6';
          }
        }}
        onMouseLeave={(e) => {
          if (!isBlockquote) {
            e.target.style.backgroundColor = '#ffffff';
          }
        }}
        title="Quote"
      >
        "
      </button>

      {/* Undo Button */}
      <button
        type="button"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().chain().focus().undo().run()}
        style={{
          ...getButtonStyle(false),
          fontSize: '14px',
          cursor: editor.can().chain().focus().undo().run() ? 'pointer' : 'not-allowed',
          opacity: editor.can().chain().focus().undo().run() ? 1 : 0.5,
        }}
        onMouseEnter={(e) => {
          if (editor.can().chain().focus().undo().run()) {
            e.target.style.backgroundColor = '#f3f4f6';
          }
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#ffffff';
        }}
        title="Undo"
      >
        ↶
      </button>

      {/* Redo Button */}
      <button
        type="button"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().chain().focus().redo().run()}
        style={{
          ...getButtonStyle(false),
          fontSize: '14px',
          cursor: editor.can().chain().focus().redo().run() ? 'pointer' : 'not-allowed',
          opacity: editor.can().chain().focus().redo().run() ? 1 : 0.5,
        }}
        onMouseEnter={(e) => {
          if (editor.can().chain().focus().redo().run()) {
            e.target.style.backgroundColor = '#f3f4f6';
          }
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#ffffff';
        }}
        title="Redo"
      >
        ↷
      </button>
    </div>
  );
};

export { EditorContent };
